import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import {
  RuleInGroupDto,
  RuleForCompositionDto,
  RuleGroupForCompositionDto,
  SqlFilterDto,
  SqlGroupDto,
  SqlOrderDto,
  CreateRuleInGroupRequest,
  UpdateRuleInGroupRequest,
  ReorderRulesRequest,
  CreateSqlFilterRequest,
  UpdateSqlFilterRequest,
  CreateSqlGroupRequest,
  UpdateSqlGroupRequest,
  CreateSqlOrderRequest,
  UpdateSqlOrderRequest,
} from '../types/ruleComposition';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class RuleCompositionService {
  private baseUrl = `${API_BASE_URL}/rule-composition`;

  // ============================================
  // Composition View Endpoints
  // ============================================

  /**
   * Get all rules available for composition (drag sources)
   */
  async getAllRules(): Promise<RuleForCompositionDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/rules`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Get all rule groups available for composition (drop targets)
   */
  async getAllGroups(): Promise<RuleGroupForCompositionDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/groups`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  // ============================================
  // Rule-in-Group Endpoints
  // ============================================

  /**
   * Get all rules linked to a specific group
   */
  async getRulesInGroup(ruleGroupId: number): Promise<RuleInGroupDto[]> {
    const response = await AuthService.authenticatedGet(
      `${this.baseUrl}/groups/${ruleGroupId}/rules`
    );
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Get a specific rule-in-group record
   */
  async getRuleInGroup(id: number): Promise<RuleInGroupDto | null> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/rule-in-group/${id}`);
    if (!response.ok) {
      if (response.status === 404) return null;
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Create a new rule-to-group link (from drag-and-drop)
   */
  async createLink(request: CreateRuleInGroupRequest): Promise<RuleInGroupDto> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/link`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Update a rule-in-group record
   */
  async updateRuleInGroup(id: number, request: UpdateRuleInGroupRequest): Promise<RuleInGroupDto> {
    const response = await AuthService.authenticatedPut(
      `${this.baseUrl}/rule-in-group/${id}`,
      request
    );
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Delete a rule-in-group record
   */
  async deleteRuleInGroup(id: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/rule-in-group/${id}`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
  }

  /**
   * Reorder rules in a group
   */
  async reorderRules(ruleGroupId: number, orderedIds: number[]): Promise<void> {
    const request: ReorderRulesRequest = {
      ruleGroupID: ruleGroupId,
      orderedRuleInGroupIds: orderedIds,
    };
    const response = await AuthService.authenticatedPost(
      `${this.baseUrl}/groups/${ruleGroupId}/reorder`,
      request
    );
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
  }

  /**
   * Check if a rule is already linked to a group
   */
  async checkLink(ruleId: number, ruleGroupId: number): Promise<boolean> {
    const response = await AuthService.authenticatedGet(
      `${this.baseUrl}/check-link?ruleId=${ruleId}&ruleGroupId=${ruleGroupId}`
    );
    if (!response.ok) {
      return false;
    }
    const result = await response.json();
    return result.isLinked;
  }

  // ============================================
  // SQL Syntax Endpoints
  // ============================================

  /**
   * Get all SQL filters for dropdown
   */
  async getSqlFilters(): Promise<SqlFilterDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/sql-filters`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Get all SQL groups for dropdown
   */
  async getSqlGroups(): Promise<SqlGroupDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/sql-groups`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Get all SQL orders for dropdown
   */
  async getSqlOrders(): Promise<SqlOrderDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/sql-orders`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  // ============================================
  // SQL Filter CRUD Endpoints
  // ============================================

  /**
   * Get a specific SQL filter by ID
   */
  async getSqlFilterById(id: number): Promise<SqlFilterDto | null> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/sql-filters/${id}`);
    if (!response.ok) {
      if (response.status === 404) return null;
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Create a new SQL filter
   */
  async createSqlFilter(request: CreateSqlFilterRequest): Promise<SqlFilterDto> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/sql-filters`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Update an existing SQL filter
   */
  async updateSqlFilter(id: number, request: UpdateSqlFilterRequest): Promise<SqlFilterDto> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/sql-filters/${id}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Delete a SQL filter
   */
  async deleteSqlFilter(id: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/sql-filters/${id}`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
  }

  // ============================================
  // SQL Group CRUD Endpoints
  // ============================================

  /**
   * Get a specific SQL group by ID
   */
  async getSqlGroupById(id: number): Promise<SqlGroupDto | null> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/sql-groups/${id}`);
    if (!response.ok) {
      if (response.status === 404) return null;
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Create a new SQL group
   */
  async createSqlGroup(request: CreateSqlGroupRequest): Promise<SqlGroupDto> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/sql-groups`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Update an existing SQL group
   */
  async updateSqlGroup(id: number, request: UpdateSqlGroupRequest): Promise<SqlGroupDto> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/sql-groups/${id}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Delete a SQL group
   */
  async deleteSqlGroup(id: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/sql-groups/${id}`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
  }

  // ============================================
  // SQL Order CRUD Endpoints
  // ============================================

  /**
   * Get a specific SQL order by ID
   */
  async getSqlOrderById(id: number): Promise<SqlOrderDto | null> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/sql-orders/${id}`);
    if (!response.ok) {
      if (response.status === 404) return null;
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Create a new SQL order
   */
  async createSqlOrder(request: CreateSqlOrderRequest): Promise<SqlOrderDto> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/sql-orders`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Update an existing SQL order
   */
  async updateSqlOrder(id: number, request: UpdateSqlOrderRequest): Promise<SqlOrderDto> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/sql-orders/${id}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Delete a SQL order
   */
  async deleteSqlOrder(id: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/sql-orders/${id}`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
  }

  // ============================================
  // SQL Testing Endpoints
  // ============================================

  /**
   * Test a rule's SQL expression with its configured filter/group/order.
   * Returns the count of rows that would be affected without executing modifications.
   */
  async testRuleSql(ruleInGroupId: number): Promise<RuleSqlTestResult> {
    const response = await AuthService.authenticatedPost(
      `${this.baseUrl}/test-sql/${ruleInGroupId}`,
      {}
    );
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }
}

// Response type for SQL test
export interface RuleSqlTestResult {
  success: boolean;
  affectedRowCount: number;
  errorMessage: string | null;
  combinedSql: string | null;
  convertedSelectQuery: string | null;
  previewRows: Record<string, unknown>[];
  columnNames: string[];
}

export const ruleCompositionService = new RuleCompositionService();
export default ruleCompositionService;
